import React from 'react';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Trash2, AlertCircle, Info } from 'lucide-react';

export default function NodeSidebar({ selectedNode, onUpdate, onDelete }) {
    if (!selectedNode) return (
        <div className="flex flex-col items-center justify-center p-8 text-center h-full text-muted-foreground">
            <Info className="h-8 w-8 mb-4 opacity-20" />
            <p className="text-sm font-medium">Select a node to configure its behavior.</p>
        </div>
    );

    const { data, type } = selectedNode;
    
    const generateActionSummary = (actionType, params) => {
        switch (actionType) {
            case 'TASK':
                if (!params.title && !params.ownerId) return 'Task details required';
                return `Title: ${params.title || 'Follow up'}\nOwner: ${params.ownerId || 'Contact Owner'}\nDue: ${params.dueDelayHours || 24}h`;
            case 'SEND_EMAIL':
            case 'REPLY':
                return `Subject: ${params.subject || 'Default Reply'}`;
            case 'WEBHOOK':
                return `Endpoint: ${params.url || 'URL missing'}`;
            case 'SEQUENCE_ENROLLMENT':
                return `Sequence: ${params.sequenceId || 'Target missing'}`;
            case 'ASSIGN_OWNER':
                return `Owner: ${params.ownerId || 'Selection required'}`;
            case 'ADD_NOTE':
                return `Note: ${params.note?.substring(0, 20) || 'Empty'}...`;
            case 'STATUS_CHANGE':
                return `Move to: ${params.status || 'Interested'}`;
            case 'TAG':
                return `Tag: ${params.tag || 'Label missing'}`;
            case 'SUPPRESS':
                return 'Global blocklist addition';
            default:
                return '';
        }
    };

    const renderHeader = () => {
        const typeInfo = {
            triggerNode: { label: 'Signal Trigger', color: 'bg-blue-500/10 text-blue-600 border-blue-200' },
            conditionNode: { label: 'Filter Condition', color: 'bg-amber-500/10 text-amber-600 border-amber-200' },
            actionNode: { label: 'Automation Action', color: 'bg-green-500/10 text-green-600 border-green-200' }
        };
        const info = typeInfo[type] || { label: type, color: 'bg-muted text-muted-foreground' };
        
        return (
            <div className="p-6 border-b flex items-start justify-between bg-card">
                <div>
                    <Badge variant="outline" className={`text-[9px] uppercase font-black px-2 py-0.5 rounded-md mb-2 ${info.color}`}>
                        {info.label}
                    </Badge>
                    <h3 className="text-base font-black tracking-tight text-foreground truncate max-w-[180px]">
                        {data.label || 'Unnamed Node'}
                    </h3>
                </div>
                <Button variant="ghost" size="icon" className="text-muted-foreground hover:text-destructive hover:bg-destructive/5 rounded-full" onClick={() => onDelete(selectedNode.id)}>
                    <Trash2 className="h-4 w-4" />
                </Button>
            </div>
        );
    };

    const Label = ({ children, help }) => (
        <div className="flex flex-col gap-1.5 mb-2">
            <span className="text-[10px] font-black uppercase tracking-widest text-muted-foreground/70">
                {children}
            </span>
            {help && <p className="text-[10px] text-muted-foreground/60 leading-normal italic">{help}</p>}
        </div>
    );

    return (
        <div className="flex flex-col h-full bg-card animate-in slide-in-from-right duration-500">
            {renderHeader()}

            <div className="flex-1 overflow-y-auto p-6 space-y-8 scrollbar-hide">
                {/* Section: Basic Identity */}
                <section>
                    <Label help="How this node appears on the canvas. Use descriptive names like 'On Interested Reply'.">
                        Display Name
                    </Label>
                    <input 
                        className="w-full h-10 px-3 rounded-xl bg-background border border-border/60 text-sm font-medium focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all outline-none"
                        value={data.label || ''}
                        onChange={e => onUpdate({ label: e.target.value })}
                        placeholder="e.g., On Active Reply"
                    />
                </section>

                <div className="h-px bg-border/40 w-full" />

                {/* Section: Node Logic */}
                {type === 'triggerNode' && (
                    <section className="space-y-6">
                        <div>
                            <Label help="The inbound signal type that will start this automation flow.">
                                Signal Type
                            </Label>
                            <select 
                                className="w-full h-10 px-3 rounded-xl bg-background border border-border/60 text-sm font-bold focus:ring-2 focus:ring-primary/20 outline-none"
                                value={data.triggerType}
                                onChange={e => {
                                    const labels = { REPLY: 'Inbound Reply', NO_REPLY: 'No Reply Received', BOUNCE: 'Email Bounced' };
                                    onUpdate({ 
                                        triggerType: e.target.value, 
                                        label: labels[e.target.value] || data.label,
                                        description: `Starts when ${labels[e.target.value]?.toLowerCase() || 'a signal'} is detected.`
                                    });
                                }}
                            >
                                <option value="REPLY">Reply Received</option>
                                <option value="NO_REPLY">No Reply After Delay</option>
                                <option value="BOUNCE">Campaign Bounce</option>
                                <option value="AUTO_REPLY">Auto-Reply (OOO)</option>
                            </select>
                        </div>
                        {data.triggerType === 'NO_REPLY' && (
                            <div className="p-4 rounded-2xl bg-indigo-500/5 border border-indigo-500/10 space-y-4">
                                <div>
                                    <Label help="How long to wait for a reply before firing this trigger. Recommended: 24-48h.">
                                        Wait Duration (Hours)
                                    </Label>
                                    <input 
                                        type="number"
                                        className="w-full h-10 px-3 rounded-xl bg-background border border-indigo-500/20 text-sm font-bold outline-none"
                                        value={data.delayHours || 24}
                                        onChange={e => onUpdate({ delayHours: parseInt(e.target.value) })}
                                    />
                                </div>
                            </div>
                        )}
                    </section>
                )}

                {type === 'actionNode' && (
                    <section className="space-y-6">
                        <div>
                            <Label help="The automated task that will be executed for the matching contact.">
                                Action Type
                            </Label>
                            <select 
                                className="w-full h-10 px-3 rounded-xl bg-background border border-border/60 text-sm font-bold focus:ring-2 focus:ring-primary/20 outline-none"
                                value={data.actionType}
                                onChange={e => {
                                    const labels = { 
                                        TASK: 'Create Task', 
                                        SEND_EMAIL: 'Send Email',
                                        WEBHOOK: 'Send Webhook',
                                        SEQUENCE_ENROLLMENT: 'Enroll in Sequence',
                                        ASSIGN_OWNER: 'Assign Owner',
                                        ADD_NOTE: 'Add Note',
                                        STATUS_CHANGE: 'Change Status', 
                                        TAG: 'Add Tag', 
                                        SUPPRESS: 'Add to Blocklist' 
                                    };
                                    const summary = generateActionSummary(e.target.value, {});
                                    onUpdate({ 
                                        actionType: e.target.value, 
                                        label: labels[e.target.value] || data.label,
                                        summary,
                                        params: {} // Reset params on type change
                                    });
                                }}
                            >
                                <optgroup label="Communication">
                                    <option value="SEND_EMAIL">Send Email Reply</option>
                                    <option value="TASK">Create Manual Task</option>
                                </optgroup>
                                <optgroup label="Automation & Integration">
                                    <option value="SEQUENCE_ENROLLMENT">Enroll in Sequence</option>
                                    <option value="WEBHOOK">Trigger Webhook</option>
                                </optgroup>
                                <optgroup label="CRM Coordination">
                                    <option value="ASSIGN_OWNER">Assign Owner</option>
                                    <option value="ADD_NOTE">Add Note</option>
                                    <option value="STATUS_CHANGE">Change Contact Status</option>
                                    <option value="TAG">Add Tag</option>
                                    <option value="SUPPRESS">Suppression (Opt-out)</option>
                                </optgroup>
                            </select>
                        </div>

                        {data.actionType === 'TASK' && (
                            <div className="p-4 rounded-2xl bg-emerald-500/5 border border-emerald-500/10 space-y-4">
                                <div>
                                    <Label help="Main objective of the task. Defaults to 'Follow up: [Original Subject]'">
                                        Task Title
                                    </Label>
                                    <input 
                                        className="w-full h-10 px-3 rounded-xl bg-background border border-emerald-500/20 text-sm font-bold outline-none"
                                        value={data.params?.title || ''}
                                        onChange={e => {
                                            const newParams = { ...data.params, title: e.target.value };
                                            onUpdate({ 
                                                params: newParams,
                                                summary: generateActionSummary(data.actionType, newParams)
                                            });
                                        }}
                                        placeholder="e.g., Call Lead ASAP"
                                     />
                                </div>
                                <div className="grid grid-cols-2 gap-4">
                                    <div>
                                        <Label help="Task priority level.">Priority</Label>
                                        <select 
                                            className="w-full h-10 px-3 rounded-xl bg-background border border-emerald-500/20 text-xs font-bold outline-none"
                                            value={data.params?.priority || 'MEDIUM'}
                                            onChange={e => {
                                                const newParams = { ...data.params, priority: e.target.value };
                                                onUpdate({ 
                                                    params: newParams,
                                                    summary: generateActionSummary(data.actionType, newParams)
                                                });
                                            }}
                                        >
                                            <option value="LOW">Low</option>
                                            <option value="MEDIUM">Medium</option>
                                            <option value="HIGH">High</option>
                                            <option value="URGENT">Urgent</option>
                                        </select>
                                    </div>
                                    <div>
                                        <Label help="Hours until due from trigger.">Due In (h)</Label>
                                        <input 
                                            type="number"
                                            className="w-full h-10 px-3 rounded-xl bg-background border border-emerald-500/20 text-xs font-bold outline-none"
                                            value={data.params?.dueDelayHours || 24}
                                            onChange={e => {
                                                const newParams = { ...data.params, dueDelayHours: parseInt(e.target.value) || 0 };
                                                onUpdate({ 
                                                    params: newParams,
                                                    summary: generateActionSummary(data.actionType, newParams)
                                                });
                                            }}
                                        />
                                    </div>
                                </div>
                            </div>
                        )}

                        {data.actionType === 'SEND_EMAIL' && (
                            <div className="p-4 rounded-2xl bg-sky-500/5 border border-sky-500/10 space-y-4">
                                <div>
                                    <Label help="Subject line for the reply. Defaults to 'Reply: [Original Subject]'">
                                        Subject Override (Optional)
                                    </Label>
                                    <input 
                                        className="w-full h-10 px-3 rounded-xl bg-background border border-sky-500/20 text-sm font-bold outline-none"
                                        value={data.params?.subject || ''}
                                        onChange={e => {
                                            const newParams = { ...data.params, subject: e.target.value };
                                            onUpdate({ 
                                                params: newParams,
                                                summary: generateActionSummary(data.actionType, newParams)
                                            });
                                        }}
                                        placeholder="Leave empty to use 'Reply: ...'"
                                    />
                                </div>
                                <div>
                                    <Label help="Content of the email reply.">
                                        Email Body
                                    </Label>
                                    <textarea 
                                        className="w-full h-32 p-3 rounded-xl bg-background border border-sky-500/20 text-sm font-bold outline-none resize-none"
                                        value={data.params?.body || ''}
                                        onChange={e => {
                                            const newParams = { ...data.params, body: e.target.value };
                                            onUpdate({ 
                                                params: newParams,
                                                summary: generateActionSummary(data.actionType, newParams)
                                            });
                                        }}
                                        placeholder="Write your automated reply here..."
                                    />
                                </div>
                            </div>
                        )}

                        {data.actionType === 'WEBHOOK' && (
                            <div className="p-4 rounded-2xl bg-purple-500/5 border border-purple-500/10 space-y-4">
                                <div>
                                    <Label help="The URL to send the POST request to.">
                                        Endpoint URL
                                    </Label>
                                    <input 
                                        className="w-full h-10 px-3 rounded-xl bg-background border border-purple-500/20 text-sm font-bold outline-none"
                                        value={data.params?.url || ''}
                                        onChange={e => {
                                            const newParams = { ...data.params, url: e.target.value };
                                            onUpdate({ 
                                                params: newParams,
                                                summary: generateActionSummary(data.actionType, newParams)
                                            });
                                        }}
                                        placeholder="https://api.example.com/webhook"
                                    />
                                </div>
                            </div>
                        )}

                        {data.actionType === 'SEQUENCE_ENROLLMENT' && (
                            <div className="p-4 rounded-2xl bg-orange-500/5 border border-orange-500/10 space-y-4">
                                <div>
                                    <Label help="The sequence to enroll the contact in.">
                                        Target Sequence ID
                                    </Label>
                                    <input 
                                        className="w-full h-10 px-3 rounded-xl bg-background border border-orange-500/20 text-sm font-bold outline-none"
                                        value={data.params?.sequenceId || ''}
                                        onChange={e => {
                                            const newParams = { ...data.params, sequenceId: e.target.value };
                                            onUpdate({ 
                                                params: newParams,
                                                summary: generateActionSummary(data.actionType, newParams)
                                            });
                                        }}
                                        placeholder="Paste Sequence MongoID here"
                                    />
                                </div>
                            </div>
                        )}

                        {data.actionType === 'ASSIGN_OWNER' && (
                            <div className="p-4 rounded-2xl bg-teal-500/5 border border-teal-500/10 space-y-4">
                                <div>
                                    <Label help="The workspace member ID to assign this contact to.">
                                        Owner ID
                                    </Label>
                                    <input 
                                        className="w-full h-10 px-3 rounded-xl bg-background border border-teal-500/20 text-sm font-bold outline-none"
                                        value={data.params?.ownerId || ''}
                                        onChange={e => {
                                            const newParams = { ...data.params, ownerId: e.target.value };
                                            onUpdate({ 
                                                params: newParams,
                                                summary: generateActionSummary(data.actionType, newParams)
                                            });
                                        }}
                                        placeholder="Paste Member MongoID here"
                                    />
                                </div>
                            </div>
                        )}

                        {data.actionType === 'ADD_NOTE' && (
                            <div className="p-4 rounded-2xl bg-amber-500/5 border border-amber-500/10 space-y-4">
                                <div>
                                    <Label help="Content to append to the contact's notes.">
                                        Note Content
                                    </Label>
                                    <textarea 
                                        className="w-full h-24 p-3 rounded-xl bg-background border border-amber-500/20 text-sm font-bold outline-none resize-none"
                                        value={data.params?.note || ''}
                                        onChange={e => {
                                            const newParams = { ...data.params, note: e.target.value };
                                            onUpdate({ 
                                                params: newParams,
                                                summary: generateActionSummary(data.actionType, newParams)
                                            });
                                        }}
                                        placeholder="Add an internal note..."
                                    />
                                </div>
                            </div>
                        )}

                        {['STATUS', 'STATUS_CHANGE'].includes(data.actionType) && (
                            <div className="p-4 rounded-2xl bg-emerald-500/5 border border-emerald-500/10">
                                <Label help="Which status should the contact be moved to?">
                                    Target Status
                                </Label>
                                <select 
                                    className="w-full h-10 px-3 rounded-xl bg-background border border-emerald-500/20 text-sm font-bold outline-none"
                                    value={data.params?.status || 'Interested'}
                                    onChange={e => {
                                        const newParams = { ...data.params, status: e.target.value };
                                        onUpdate({ 
                                            params: newParams,
                                            summary: generateActionSummary(data.actionType, newParams)
                                        });
                                    }}
                                >
                                    <option value="Interested">Interested</option>
                                    <option value="Not Interested">Not Interested</option>
                                    <option value="Replied">Replied</option>
                                    <option value="Closed">Closed</option>
                                </select>
                            </div>
                        )}

                        {data.actionType === 'TAG' && (
                            <div className="p-4 rounded-2xl bg-pink-500/5 border border-pink-500/10">
                                <Label help="This tag will be appended to the contact's existing tags.">
                                    Tag to Add
                                </Label>
                                <input 
                                    className="w-full h-10 px-3 rounded-xl bg-background border border-pink-500/20 text-sm font-bold outline-none"
                                    value={data.params?.tag || ''}
                                    onChange={e => {
                                        const newParams = { ...data.params, tag: e.target.value };
                                        onUpdate({ 
                                            params: newParams,
                                            summary: generateActionSummary(data.actionType, newParams)
                                        });
                                    }}
                                    placeholder="e.g., hot-lead"
                                />
                            </div>
                        )}
                    </section>
                )}

                {type === 'conditionNode' && (
                    <section className="space-y-6">
                        <div className="flex items-center justify-between mb-4">
                            <Label help="How multiple conditions are combined.">
                                Match Strategy
                            </Label>
                            <div className="flex bg-muted rounded-lg p-1">
                                <Button 
                                    variant={ (data.params?.logicalOperator || 'AND') === 'AND' ? 'default' : 'ghost'} 
                                    size="sm" 
                                    className="h-7 text-[9px] font-black px-3 rounded-md uppercase"
                                    onClick={() => onUpdate({ params: { ...data.params, logicalOperator: 'AND' } })}
                                >
                                    All (AND)
                                </Button>
                                <Button 
                                    variant={ (data.params?.logicalOperator || 'AND') === 'OR' ? 'default' : 'ghost'} 
                                    size="sm" 
                                    className="h-7 text-[9px] font-black px-3 rounded-md uppercase"
                                    onClick={() => onUpdate({ params: { ...data.params, logicalOperator: 'OR' } })}
                                >
                                    Any (OR)
                                </Button>
                            </div>
                        </div>

                        <div className="space-y-4">
                            {(data.params?.conditions || (data.params?.field ? [data.params] : [{ field: 'status', operator: 'equals', value: '' }])).map((condition, idx) => (
                                <div key={idx} className="p-4 rounded-2xl bg-amber-500/5 border border-amber-500/10 space-y-4 relative group/cond">
                                    <div className="flex items-start justify-between gap-4">
                                        <div className="flex-1 space-y-4">
                                            <div>
                                                <span className="text-[9px] font-black uppercase tracking-widest text-muted-foreground/50 block mb-2">Field</span>
                                                <select 
                                                    className="w-full h-9 px-3 rounded-xl bg-background border border-amber-500/20 text-xs font-bold outline-none"
                                                    value={condition.field}
                                                    onChange={e => {
                                                        const newConds = [...(data.params?.conditions || [condition])];
                                                        newConds[idx] = { ...condition, field: e.target.value };
                                                        onUpdate({ params: { ...data.params, conditions: newConds } });
                                                    }}
                                                >
                                                    <optgroup label="Contact Properties">
                                                        <option value="status">Status</option>
                                                        <option value="tags">Tags</option>
                                                        <option value="email">Email Address</option>
                                                    </optgroup>
                                                    <optgroup label="Signal Context">
                                                        <option value="subject">Subject</option>
                                                        <option value="body">Body (Plain Text)</option>
                                                        <option value="from">Sender Email</option>
                                                        <option value="fromDomain">Sender Domain</option>
                                                        <option value="classification">Signal Logic / Intent (Reply/Bounce/OOO)</option>
                                                    </optgroup>
                                                </select>
                                                {condition.field === 'classification' && (
                                                    <p className="mt-1 text-[9px] text-amber-600 font-bold">
                                                        Note: For Bounces, this differentiates Hard vs Soft. For Replies, this matches AI-detected Intent.
                                                    </p>
                                                )}
                                                {condition.field === 'body' && (
                                                    <p className="mt-1 text-[9px] text-muted-foreground font-black italic">
                                                        Heuristic: No-Reply signals contain system-generated context in the body.
                                                    </p>
                                                )}
                                            </div>
                                            <div>
                                                <span className="text-[9px] font-black uppercase tracking-widest text-muted-foreground/50 block mb-2">Operator</span>
                                                <select 
                                                    className="w-full h-9 px-3 rounded-xl bg-background border border-amber-500/20 text-xs font-bold outline-none"
                                                    value={condition.operator}
                                                    onChange={e => {
                                                        const newConds = [...(data.params?.conditions || [condition])];
                                                        newConds[idx] = { ...condition, operator: e.target.value };
                                                        onUpdate({ params: { ...data.params, conditions: newConds } });
                                                    }}
                                                >
                                                    <option value="equals">Equals</option>
                                                    <option value="not_equals">Does Not Equal</option>
                                                    <option value="contains">Contains</option>
                                                    <option value="not_contains">Does Not Contain</option>
                                                    <option value="starts_with">Starts With</option>
                                                    <option value="ends_with">Ends With</option>
                                                    <option value="exists">Exists</option>
                                                    <option value="not_exists">Does Not Exist</option>
                                                    <option value="matches">Matches (Regex)</option>
                                                    <option value="in">One of (Comma Separated)</option>
                                                </select>
                                            </div>
                                            {!['exists', 'not_exists'].includes(condition.operator) && (
                                                <div>
                                                    <span className="text-[9px] font-black uppercase tracking-widest text-muted-foreground/50 block mb-2">Value</span>
                                                    <input 
                                                        className="w-full h-9 px-3 rounded-xl bg-background border border-amber-500/20 text-xs font-bold outline-none"
                                                        value={condition.value || ''}
                                                        onChange={e => {
                                                            const newConds = [...(data.params?.conditions || [condition])];
                                                            newConds[idx] = { ...condition, value: e.target.value };
                                                            const summary = newConds.map(c => `${c.field} ${c.operator} ${c.value || ''}`).join(` ${data.params?.logicalOperator || 'AND'} `);
                                                            onUpdate({ 
                                                                params: { ...data.params, conditions: newConds },
                                                                summary: summary.length > 50 ? summary.substring(0, 47) + '...' : summary
                                                            });
                                                        }}
                                                        placeholder="Value to match..."
                                                    />
                                                </div>
                                            )}
                                        </div>
                                        <Button 
                                            variant="ghost" 
                                            size="icon" 
                                            className="h-8 w-8 text-muted-foreground opacity-0 group-hover/cond:opacity-100 transition-opacity"
                                            onClick={() => {
                                                const newConds = (data.params?.conditions || [condition]).filter((_, i) => i !== idx);
                                                onUpdate({ params: { ...data.params, conditions: newConds } });
                                            }}
                                        >
                                            <Trash2 className="h-3.5 w-3.5" />
                                        </Button>
                                    </div>
                                </div>
                            ))}

                            <Button 
                                variant="outline" 
                                size="sm" 
                                className="w-full h-9 rounded-xl border-dashed border-amber-500/30 text-amber-600 hover:bg-amber-500/5 font-black uppercase text-[9px] tracking-widest"
                                onClick={() => {
                                    const current = data.params?.conditions || (data.params?.field ? [data.params] : []);
                                    onUpdate({ 
                                        params: { 
                                            ...data.params, 
                                            conditions: [...current, { field: 'subject', operator: 'contains', value: '' }] 
                                        } 
                                    });
                                }}
                            >
                                <AlertCircle className="h-3.5 w-3.5 mr-2" /> Add Condition Row
                            </Button>
                        </div>
                    </section>
                )}
            </div>

            <div className="p-6 bg-muted/20 border-t border-border/40">
                <div className="flex items-start gap-3 p-4 rounded-2xl bg-card border border-border/60 shadow-sm">
                    <Info className="h-4 w-4 shrink-0 mt-0.5 text-primary" />
                    <p className="text-[10px] leading-relaxed font-medium text-muted-foreground/80">
                        Changes to node configuration are applied to your draft automatically. <span className="text-foreground font-bold">Publishing</span> will make this flow live for all incoming signals.
                    </p>
                </div>
            </div>
        </div>
    );
}
